import axios from 'axios';
import { useAuthStore } from '../store/auth.store.js';
import type { Account, TenantMembership, UserRole } from '@atomfit/shared';
import { getRuntimeConfig } from '../config/runtime.js';

function normalizeApiBaseUrl(value: string | undefined) {
  const baseUrl = value?.replace(/\/+$/, '');

  if (!baseUrl) {
    return '/api/v1';
  }

  if (baseUrl.endsWith('/api/v1')) {
    return baseUrl;
  }

  return `${baseUrl}/api/v1`;
}

const BASE_URL = normalizeApiBaseUrl(getRuntimeConfig('VITE_API_URL'));

interface RefreshSessionResponse {
  accessToken: string;
  refreshToken: string;
  account: Account;
  role: UserRole | null;
  tenant?: { slug: string };
  availableTenants: TenantMembership[];
}

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const { accessToken, tenantSlug } = useAuthStore.getState();
  const isPublicGymRead = config.method?.toLowerCase() === 'get' && config.url?.startsWith('/gyms');

  if (accessToken) config.headers['Authorization'] = `Bearer ${accessToken}`;
  if (tenantSlug && !isPublicGymRead && !config.headers['X-Tenant-ID']) {
    config.headers['X-Tenant-ID'] = tenantSlug;
  }
  return config;
});

apiClient.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      const { refreshToken, setTokens, setAccount, setRole, setTenantSlug, setAvailableTenants, logout } = useAuthStore.getState();
      if (refreshToken) {
        try {
          const res = await axios.post<RefreshSessionResponse>(
            `${BASE_URL}/auth/refresh`,
            { refreshToken },
          );
          setTokens(res.data.accessToken, res.data.refreshToken);
          setAccount(res.data.account);
          setRole(res.data.role);
          setTenantSlug(res.data.tenant?.slug ?? null);
          setAvailableTenants(res.data.availableTenants);
          original.headers['Authorization'] = `Bearer ${res.data.accessToken}`;
          return apiClient(original);
        } catch {
          logout();
        }
      }
    }
    return Promise.reject(error);
  },
);
